import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCookieConsent, CookiePreferences } from '@/hooks/useCookieConsent';
import { Language } from '@/types';

// Custom Checkbox component imitating iOS/Apple UI
const CookieToggle: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  lang: Language;
}> = ({ title, description, checked, disabled, onChange, lang }) => {
  const isRtl = lang === 'ar';
  return (
    <div className={`flex items-start gap-4 ${disabled ? 'opacity-70' : ''}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`mt-1 flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
          checked 
            ? 'bg-white border-white dark:bg-white dark:border-white' 
            : 'bg-transparent border-slate-400 dark:border-slate-500'
        }`}
      >
        {checked && (
          <svg className="w-3.5 h-3.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      <div className={`flex flex-col gap-1 ${isRtl ? 'text-right' : 'text-left'}`}>
        <span className="text-sm font-semibold text-slate-900 dark:text-white leading-none mt-1.5">{title}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mt-1.5 max-w-sm">
          {description}
        </span>
      </div>
    </div>
  );
};

export const CookieManager: React.FC<{ lang: Language }> = ({ lang }) => {
  const { preferences, hasConsented, isModalOpen, closeModal, savePreferences, acceptAll, openModal } = useCookieConsent();
  const [localPrefs, setLocalPrefs] = useState<CookiePreferences>(preferences);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Sync local state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setLocalPrefs(preferences);
    }
  }, [isModalOpen, preferences]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isModalOpen]);

  // Global listener to open modal from anywhere
  useEffect(() => {
    const handleOpen = () => openModal();
    window.addEventListener('open-cookie-manager', handleOpen);
    return () => window.removeEventListener('open-cookie-manager', handleOpen);
  }, [openModal]);

  const handleSave = () => {
    savePreferences(localPrefs);
  };

  const isRtl = lang === 'ar';

  const content = {
    en: {
      title: "Cookie Preferences",
      desc: "Websites and apps use cookies and other identifiers to store and retrieve information on your device. Some of this information may be shared with third parties for different purposes. Use the tool below to manage your preferences. You can change them anytime.",
      necessary: {
        title: "Strictly necessary",
        desc: "These cookies are required for the site to work and can't be turned off. They support essential functions like security, user authentication, and customer support."
      },
      analytics: {
        title: "Analytics Cookies",
        desc: "These cookies help us understand how visitors interact with our site. They allow us to measure traffic and improve site performance."
      },
      marketing: {
        title: "Marketing measurement",
        desc: "These cookies help us measure the effectiveness of our marketing campaigns."
      },
      personalized: {
        title: "Personalized marketing",
        desc: "This helps us personalize and measure VEXL's own marketing on third-party platforms."
      },
      done: "Done"
    },
    ar: {
      title: "تفضيلات ملفات تعريف الارتباط",
      desc: "تستخدم مواقع الويب والتطبيقات ملفات تعريف الارتباط والمعرفات الأخرى لتخزين واسترداد المعلومات على جهازك. قد تتم مشاركة بعض هذه المعلومات مع أطراف ثالثة لأغراض مختلفة. استخدم الأداة أدناه لإدارة تفضيلاتك. يمكنك تغييرها في أي وقت.",
      necessary: {
        title: "ضرورية جداً",
        desc: "هذه الملفات مطلوبة لعمل الموقع ولا يمكن إيقاف تشغيلها. إنها تدعم الوظائف الأساسية مثل الأمان، مصادقة المستخدم، ودعم العملاء."
      },
      analytics: {
        title: "ملفات التحليل",
        desc: "تساعدنا هذه الملفات على فهم كيفية تفاعل الزوار مع موقعنا. تتيح لنا قياس حركة المرور وتحسين أداء الموقع."
      },
      marketing: {
        title: "قياس التسويق",
        desc: "تساعدنا هذه الملفات على قياس فعالية حملاتنا التسويقية."
      },
      personalized: {
        title: "التسويق المخصص",
        desc: "يساعدنا هذا على تخصيص وقياس تسويق VEXL الخاص على المنصات الخارجية."
      },
      done: "تم"
    }
  }[lang];

  if (!mounted) return null;

  return createPortal(
    <>
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="relative w-full max-w-[420px] max-h-[85vh] flex flex-col bg-white dark:bg-[#111] border border-slate-200 dark:border-white/[0.08] shadow-2xl rounded-3xl overflow-hidden"
              dir={isRtl ? 'rtl' : 'ltr'}
            >
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-8 pb-2">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">
                  {content.title}
                </h2>
                <p className="text-[11px] md:text-xs text-slate-500 dark:text-[#888] mb-8 leading-relaxed">
                  {content.desc}
                </p>

                <div className="flex flex-col gap-6">
                  <CookieToggle
                    title={content.necessary.title}
                    description={content.necessary.desc}
                    checked={true}
                    disabled={true}
                    onChange={() => {}}
                    lang={lang}
                  />
                  
                  <CookieToggle
                    title={content.analytics.title}
                    description={content.analytics.desc}
                    checked={localPrefs.analytics}
                    onChange={(c) => setLocalPrefs(prev => ({ ...prev, analytics: c }))}
                    lang={lang}
                  />
                  
                  <CookieToggle
                    title={content.marketing.title}
                    description={content.marketing.desc}
                    checked={localPrefs.marketing}
                    onChange={(c) => setLocalPrefs(prev => ({ ...prev, marketing: c }))}
                    lang={lang}
                  />
                  
                  <CookieToggle
                    title={content.personalized.title}
                    description={content.personalized.desc}
                    checked={localPrefs.personalized}
                    onChange={(c) => setLocalPrefs(prev => ({ ...prev, personalized: c }))}
                    lang={lang}
                  />
                </div>
              </div>

              {/* Fixed Footer */}
              <div className="shrink-0 p-6 md:px-8 md:pb-8 md:pt-4 bg-white dark:bg-[#111] border-t border-transparent dark:border-white/[0.02] z-10">
                <button
                  onClick={handleSave}
                  className="w-full py-3.5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-black font-semibold text-sm hover:scale-[1.02] active:scale-95 transition-all shadow-lg"
                >
                  {content.done}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Subtle bottom banner if not consented yet */}
      <AnimatePresence>
        {!hasConsented && !isModalOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-xs z-[99998] bg-white dark:bg-[#111]/90 backdrop-blur-xl border border-slate-200 dark:border-white/[0.1] shadow-2xl rounded-2xl p-4"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-3 leading-relaxed font-medium">
              {lang === 'ar' 
                ? 'نحن نستخدم ملفات تعريف الارتباط لتحسين تجربتك.' 
                : 'We use cookies to enhance your experience.'}
            </p>
            <div className="flex gap-2">
              <button 
                onClick={acceptAll}
                className="flex-1 py-2 rounded-lg bg-cyan-600 text-white text-[11px] font-semibold hover:bg-cyan-700 transition-colors"
              >
                {lang === 'ar' ? 'قبول الكل' : 'Accept All'}
              </button>
              <button 
                onClick={openModal}
                className="flex-1 py-2 rounded-lg bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-white text-[11px] font-semibold hover:bg-slate-200 dark:hover:bg-white/20 transition-colors"
              >
                {lang === 'ar' ? 'خيارات' : 'Manage'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};
